import { FeatureFlags, sentryDsn } from 'application/constants/env-variables';
import { Loading } from 'components/uikit/suspense/Loading';
import React, { Suspense, useEffect, useMemo } from 'react';
import { Provider } from 'react-redux';
import { LanguageProvider } from './application/localization/LanguageProvider';
import { PersistGate } from 'redux-persist/integration/react';
import { anonymousRoutes, authorizedRoutes } from 'pages/router';
import axios from 'axios';
import { QueryFactory } from './services/api';
import * as Sentry from '@sentry/react';
import { RootStore } from './application/redux-store';
import { sessionAxiosInterceptor } from './helpers/interceptors/inject-session-interceptor';
import { injectLanguageInterceptor } from './helpers/interceptors/inject-language-interceptor';
import {
  setupAuthInterceptor,
  useAuth,
  useIsAuthorized,
} from 'helpers/auth/auth-interceptor';
import { sendRefreshTokenRequest } from 'helpers/auth/auth-client';
import { logoutAction } from './application/redux-store/root-reducer';
import { backendUri } from 'helpers/auth/openid/openid-settings';
import { ThemeProvider } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { ModalProvider } from './components/uikit/modal/useModal';
import { MiniProfiler, miniProfilerInterceptor } from './helpers/MiniProfiler';
import { RouterProvider } from 'react-router-dom';
import { blobResponseErrorInterceptor } from 'helpers/interceptors/blob-error-interceptor';
import { config } from 'devextreme/common';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { addLogoutHandler } from 'helpers/auth/auth-handlers';
import { useAnalytics } from 'helpers/analytics';
import { getCurrentSupportedLanguage } from 'application/localization/localization';
import { useTranslation } from 'react-i18next';

config({
  licenseKey:
    'ewogICJmb3JtYXQiOiAxLAogICJjdXN0b21lcklkIjogImFhNTlmMjNiLTBhNzAtNDFkOS1iN2NlLWYxZjZlY2IzZDIxZiIsCiAgIm1heFZlcnNpb25BbGxvd2VkIjogMjUyCn0=.W5WcBud4lqIf1vqO+reCGYAerT2iO3Ssppe5XvpLVWVXBpjUGy7bVvw3yCQsn0Q3Z/apvFq+khQSeXAoDmcUPfu8FvaLZ2aGPQdZZD/o7imEPehGA9zU8owQE3fc4zXfwhxkTA==',
});

QueryFactory.setAxiosFactory(() => axios);

setupAuthInterceptor(axios, async (authData) => {
  const result = await sendRefreshTokenRequest(authData.refresh_token);
  return result;
});
addLogoutHandler(() => {
  RootStore.store.dispatch(logoutAction);
  window.history.pushState(null, '', backendUri);
});
axios.interceptors.request.use(injectLanguageInterceptor);
axios.interceptors.request.use(sessionAxiosInterceptor);
axios.interceptors.response.use(null, blobResponseErrorInterceptor);
if (FeatureFlags.isMiniProfilerEnabled()) {
  axios.interceptors.response.use(miniProfilerInterceptor, undefined);
}

if (sentryDsn()) {
  Sentry.init({
    dsn: sentryDsn(),
    // beforeSend(event, hint) {
    //   console.log('Sentry before send', event, hint);
    //   if (event.exception && event.event_id) {
    //     // Sentry.showReportDialog({ eventId: event.event_id });
    //     // Sentry.captureFeedback({
    //     //   name: 'userName',
    //     //   email: 'userEmail',
    //     //   message: 'comments',
    //     //   associatedEventId: event.event_id,
    //     // });
    //   }
    //   return event;
    // },
    integrations: [
      Sentry.browserTracingIntegration(),
      // Sentry.feedbackIntegration({
      //   colorScheme: 'system',
      // }),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ['leocare-application-key'],
        behaviour: 'drop-error-if-contains-third-party-frames',
      }),
    ],
    tracesSampleRate: 1.0,
  });
}

const theme = createTheme();

export const App = () => {
  const fallback = useMemo(() => {
    return <Loading loading={true} />;
  }, []);

  const i18n = useTranslation();
  const isAuth = useIsAuthorized();
  const authData = useAuth();
  const { identifyUser } = useAnalytics();
  const router = useMemo(
    () => (isAuth ? authorizedRoutes() : anonymousRoutes()),
    [isAuth],
  );

  useEffect(() => {
    if (authData === 'loading') return;
    const customProps = {
      userId: authData?.claims.id ?? '',
      roles: authData?.claims.roles ?? [],
      language: getCurrentSupportedLanguage(i18n.i18n),
    };
    identifyUser(customProps.userId, customProps);
  }, [authData, i18n]);

  if (isAuth === 'loading') return <Loading loading={true} />;

  return (
    <Suspense fallback={fallback}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Provider store={RootStore.store}>
            <PersistGate loading={fallback} persistor={RootStore.persistor}>
              <LanguageProvider>
                <ModalProvider>
                  <RouterProvider router={router} />

                  {FeatureFlags.isMiniProfilerEnabled() && <MiniProfiler />}
                </ModalProvider>
              </LanguageProvider>
            </PersistGate>
          </Provider>
        </LocalizationProvider>
      </ThemeProvider>
    </Suspense>
  );
};
